import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, SearchXIcon, UsersIcon } from 'lucide-react';
import { useHousing } from '../../contexts/HousingContext';
import { campusById } from '../../data/institutions';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PropertyCard } from './PropertyCard';
import { HousingMap } from './HousingMap';
import { HousingCategoryTabs, HousingPrimaryFilters, HousingSearchField, HousingSort, HousingViewToggle, MoreFiltersDialog, SearchAsIMove } from './HousingControls';

/**
 * Composition B — the discovery finder.
 *
 * Same data, same state, different argument: it opens on the promise rather
 * than the workspace, puts category first because most students know the KIND
 * of home they want before anything else, and keeps the map alongside as a
 * quiet companion with a commute prompt.
 */
export function FindHomeVariantB() {
  const {
    results,
    intent,
    activeId,
    setActiveId,
    hoverId,
    setHoverId,
    saved,
    toggleSaved,
    view
  } = useHousing();
  const [moreOpen, setMoreOpen] = useState(false);
  const place = campusById(intent.campusId);
  return <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6" data-id="element-1838" data-mp="38cbcu72udp2j">
      <header className="max-w-2xl" data-id="element-1839" data-mp="38cl561bd08be">
        <h1 className="text-h1 font-semibold text-ink" data-id="element-1840" data-mp="34azjbqgukjp2">Find a home that fits your life</h1>
        <p className="mt-2 text-body-lg text-ink-muted" data-id="element-1841" data-mp="4vl1dcohqjtg">
          Student housing near your campus. Safe, comfortable and affordable.
        </p>
      </header>

      <div className="mt-5 space-y-4" data-id="element-1842" data-mp="3w2ffab4aniry">
        <HousingSearchField className="max-w-2xl" data-id="element-1843" data-mp="3rb5r7eye8ni6" />
        <HousingCategoryTabs data-id="element-1844" data-mp="3r9mbbpgngegx" />
        <HousingPrimaryFilters onOpenMore={() => setMoreOpen(true)} data-id="element-1845" data-mp="3r8szwb6qx0sg" />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-id="element-1846" data-mp="3n9bv7jz587oc">
        <p className="text-body font-medium text-ink" data-id="element-1847" data-mp="nmvpxn6quki6">
          <span className="cz-num" data-id="element-1848" data-mp="ivm3evghrxai">{results.length}</span>{' '}
          {results.length === 1 ? 'home' : 'homes'} found
        </p>
        <div className="flex flex-wrap items-center gap-3" data-id="element-1849" data-mp="2d49407ei90e4">
          <HousingSort data-id="element-1850" data-mp="2i2auvei6a405" />
          <HousingViewToggle data-id="element-1851" data-mp="2i2kemkcmjexe" />
        </div>
      </div>

      <div className={`mt-4 gap-6 ${view === 'split' ? 'lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]' : ''}`} data-id="element-1852" data-mp="2i458e7r46tu7">
        {view !== 'map' && <div className="min-w-0 space-y-4" data-id="element-1853" data-mp="28aemnamdgxi7">
            {results.length === 0 ? <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1855" data-mp="1zbt77oc75yln" />} title="Nothing matches yet" description="No home around this campus fits every condition. Loosen one — usually the rent ceiling or the distance — and the rest of your search stays as it is." data-id="element-1854" data-mp="1ub59kjtgqjig" /> : results.map(listing => <PropertyCard key={listing.id} listing={listing} layout="row" selected={activeId === listing.id} saved={saved.includes(listing.id)} onToggleSave={toggleSaved} onHover={setHoverId} onSelect={setActiveId} data-id="element-1856" data-mp="3etuyr0sgophn" />)}
          </div>}

        {view !== 'list' && place && <div className={view === 'map' ? 'mt-4' : ''} data-id="element-1857" data-mp="1d349smvjo8kl">
            <div className="sticky top-24 space-y-4" data-id="element-1858" data-mp="1d1b4pc0kxwep">
              <div className={`relative overflow-hidden rounded-2xl border border-line ${view === 'map' ? 'h-[70dvh]' : 'h-[520px]'}`} data-id="element-1859" data-mp="1qtra2jgye6is">
                <HousingMap listings={results} campus={{
              lat: place.campus.lat,
              lng: place.campus.lng,
              name: place.campus.name
            }} activeId={activeId} hoverId={hoverId} onSelect={setActiveId} data-id="element-1860" data-mp="20rni32elglco" />
                <div className="pointer-events-auto absolute left-3 top-3 z-[400]" data-id="element-1861" data-mp="2xjh9z9dzwvl">
                  <SearchAsIMove data-id="element-1862" data-mp="7vupk5k19zbp" />
                </div>
              </div>

              {view === 'split' && <section className="rounded-2xl border border-line bg-surface p-4" data-id="element-1863" data-mp="cuxttns6w1im">
                  <p className="flex gap-3" data-id="element-1864" data-mp="hu0nyd6msnbt">
                    <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700" data-id="element-1865" data-mp="htsc8om8vo27">
                      <ClockIcon className="h-5 w-5" data-id="element-1866" data-mp="3lbaiirmdcn0i" />
                    </span>
                    <span data-id="element-1867" data-mp="3q9c1lcjsombz">
                      <span className="block text-body font-semibold text-ink" data-id="element-1868" data-mp="3q80w98apb6ir">
                        Living near campus?
                      </span>
                      <span className="block text-caption leading-snug text-ink-muted" data-id="element-1869" data-mp="2xm81nlzk6y72">
                        Distances are straight-line to the main gate, not travel time — traffic
                        around Yaba makes those two very different numbers.
                      </span>
                    </span>
                  </p>
                  <div className="mt-3" data-id="element-1870" data-mp="1fol4p65wzq2x">
                    <Button size="sm" variant="secondary" onClick={() => setMoreOpen(true)} data-id="element-1871" data-mp="1aogozictyzdr">
                      Set distance preference
                    </Button>
                  </div>
                </section>}
            </div>
          </div>}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-brand-100 bg-brand-50 p-5" data-id="element-1872" data-mp="vzu99fjak5m0">
        <p className="max-w-xl text-body text-ink" data-id="element-1873" data-mp="rxft5370bjyf">
          Found somewhere you like but need to split the rent? Match with students whose budget,
          schedule and habits line up with yours, then apply as a household.
        </p>
        <span className="flex flex-wrap gap-2" data-id="element-1874" data-mp="hz9owexqa1qe">
          <Link to="/housing/roommates" data-id="element-1875" data-mp="d0gcyqy3aisc">
            <Button variant="primary" icon={<UsersIcon className="h-4 w-4" data-id="element-1877" data-mp="d3ayklsua9nc" />} data-id="element-1876" data-mp="d1hzbrkk2rpd">
              Find your people
            </Button>
          </Link>
          <Link to="/housing/household" data-id="element-1878" data-mp="8c5ul598ot56">
            <Button variant="secondary" data-id="element-1879" data-mp="8d8lhd6kavl3">My household</Button>
          </Link>
        </span>
      </div>

      <MoreFiltersDialog open={moreOpen} onClose={() => setMoreOpen(false)} data-id="element-1880" data-mp="3qjmtywm2adc3" />
    </div>;
}